import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { useMyRestaurants } from '../hooks/useMyRestaurants';
import { formatMoney } from '../lib/format';
import { PaymentQr, type PaymentConfig } from '../components/PaymentQr';
import { AddItemsPanel } from '../components/AddItemsPanel';

interface Tab {
  id: string; seq: number; status: string; total: number;
  serviceApplied: boolean | null; couvertQty: number; bonifId: string | null;
}
interface ReadyItem { id: string; name: string; qty: number; tab: number }
interface Session {
  id: string; tableId: string; label: string; isFloating: boolean; billRequested: boolean;
  tabs: Tab[]; ready: ReadyItem[]; total: number;
}
interface FreeTable { id: string; label: string }

export function SessionsPage() {
  const { t } = useTranslation();
  const { restaurants, loading } = useMyRestaurants();
  const restaurant = restaurants[0];
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [payCfg, setPayCfg] = useState<PaymentConfig | null>(null);
  const [charge, setCharge] = useState<{ amountCents: number; ref: string; tabId?: string } | null>(null);
  const [bonifList, setBonifList] = useState<Array<{ id: string; name: string }>>([]);
  const [serviceDefault, setServiceDefault] = useState(false);
  const [moveTargets, setMoveTargets] = useState<FreeTable[]>([]);
  const [floatTables, setFloatTables] = useState<FreeTable[]>([]);
  const [movePick, setMovePick] = useState('');
  const [floatPick, setFloatPick] = useState('');
  const [openPick, setOpenPick] = useState('');
  const [addingFor, setAddingFor] = useState<string | null>(null);
  const [soundOn, setSoundOn] = useState(true);
  const audioRef = useRef<AudioContext | null>(null);

  const reload = useCallback(async () => {
    if (!restaurant) return;
    const { data } = await supabase
      .from('table_sessions')
      .select('id, table_id, bill_requested_at, tables(label, is_floating), tabs(id, seq, status, service_applied, couvert_qty, bonification_id, order_items(id, name_snapshot, quantity, unit_price_cents, kitchen_status))')
      .eq('restaurant_id', restaurant.id).eq('status', 'open').order('opened_at');
    let list: Session[] = [];
    if (data) {
      list = (data as unknown as Array<Record<string, unknown>>).map((s) => {
        const tabsRaw = (s.tabs as Array<Record<string, unknown>>) ?? [];
        const ready: ReadyItem[] = [];
        const tabs: Tab[] = tabsRaw.map((tb) => {
          const items = (tb.order_items as Array<{ id: string; name_snapshot: string; quantity: number; unit_price_cents: number; kitchen_status: string }>) ?? [];
          const total = items.filter((i) => i.kitchen_status !== 'cancelled').reduce((sum, i) => sum + i.unit_price_cents * i.quantity, 0);
          items.filter((i) => i.kitchen_status === 'ready').forEach((i) => ready.push({ id: i.id, name: i.name_snapshot, qty: i.quantity, tab: tb.seq as number }));
          return {
            id: tb.id as string, seq: tb.seq as number, status: tb.status as string, total,
            serviceApplied: (tb.service_applied as boolean | null) ?? null,
            couvertQty: (tb.couvert_qty as number) ?? 0, bonifId: (tb.bonification_id as string | null) ?? null
          };
        });
        const tbl = s.tables as { label?: string; is_floating?: boolean } | null;
        return {
          id: s.id as string, tableId: s.table_id as string, label: tbl?.label ?? '—', isFloating: Boolean(tbl?.is_floating),
          billRequested: Boolean(s.bill_requested_at), tabs: tabs.sort((a, b) => a.seq - b.seq), ready,
          total: tabs.reduce((x, tb) => x + tb.total, 0)
        };
      });
      setSessions(list);
    }
    const { data: tbs } = await supabase.from('tables').select('id, label, is_floating').eq('restaurant_id', restaurant.id).eq('is_active', true).order('label');
    const busy = new Set(list.map((s) => s.tableId));
    const all = (tbs ?? []) as Array<{ id: string; label: string; is_floating: boolean }>;
    setMoveTargets(all.filter((tb) => !tb.is_floating && !busy.has(tb.id)).map(({ id, label }) => ({ id, label })));
    setFloatTables(all.filter((tb) => tb.is_floating).map(({ id, label }) => ({ id, label })));
  }, [restaurant]);

  useEffect(() => {
    void reload();
    if (!restaurant) return;
    void (async () => {
      const { data: r } = await supabase.from('restaurants').select('currency, locale, service_charge_default').eq('id', restaurant.id).single();
      const { data: p } = await supabase.from('restaurant_payment').select('*').eq('restaurant_id', restaurant.id).maybeSingle();
      const { data: b } = await supabase.from('bonifications').select('id, name').eq('restaurant_id', restaurant.id).eq('is_active', true).order('created_at');
      if (r) {
        setServiceDefault(Boolean(r.service_charge_default));
        setPayCfg({ currency: r.currency, locale: r.locale, pix_key: p?.pix_key ?? null, pix_merchant_name: p?.pix_merchant_name ?? null, pix_city: p?.pix_city ?? null, sepa_iban: p?.sepa_iban ?? null, sepa_name: p?.sepa_name ?? null });
      }
      if (b) setBonifList(b as Array<{ id: string; name: string }>);
    })();
    const ch = supabase.channel(`salao-${restaurant.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'table_sessions', filter: `restaurant_id=eq.${restaurant.id}` }, () => void reload())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'order_items', filter: `restaurant_id=eq.${restaurant.id}` }, () => void reload())
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [restaurant, reload]);

  const readyTotal = sessions.reduce((n, s) => n + s.ready.length, 0);
  // sino contínuo enquanto houver item pronto pra entregar
  useEffect(() => {
    if (!soundOn || readyTotal === 0) return;
    const beep = () => {
      try {
        if (!audioRef.current) audioRef.current = new AudioContext();
        const ctx = audioRef.current; void ctx.resume();
        const osc = ctx.createOscillator(); const gain = ctx.createGain();
        osc.frequency.value = 880; gain.gain.value = 0.12; osc.connect(gain); gain.connect(ctx.destination);
        osc.start(); osc.stop(ctx.currentTime + 0.22);
      } catch { /* autoplay bloqueado até interação */ }
    };
    beep(); const id = window.setInterval(beep, 2200); return () => window.clearInterval(id);
  }, [soundOn, readyTotal]);

  async function closeTab(id: string, method = 'manual') { await supabase.rpc('close_tab', { p_tab_id: id, p_payment_method: method }); setCharge(null); void reload(); }
  async function closeSession(id: string, method = 'manual') { await supabase.rpc('close_session', { p_session_id: id, p_payment_method: method }); setSelected(null); void reload(); }
  async function updateTab(id: string, patch: Record<string, unknown>) { await supabase.from('tabs').update(patch).eq('id', id); void reload(); }
  async function deliver(itemId: string) { await supabase.from('order_items').update({ kitchen_status: 'served' }).eq('id', itemId); void reload(); }
  async function moveSession(id: string) { if (!movePick) return; await supabase.rpc('move_session', { p_session_id: id, p_table_id: movePick }); setMovePick(''); void reload(); }
  async function openFloating() { if (!floatPick) return; const { data } = await supabase.rpc('staff_open_session', { p_table_id: floatPick }); setFloatPick(''); await reload(); const sid = (data as { session_id?: string } | null)?.session_id; if (sid) setSelected(sid); }
  async function openTable() { if (!openPick) return; const { data } = await supabase.rpc('staff_open_table', { p_table_id: openPick }); setOpenPick(''); await reload(); const sid = (data as { session_id?: string } | null)?.session_id; if (sid) setSelected(sid); }
  async function cobrarTab(tabId: string, ref: string) { const { data } = await supabase.rpc('tab_bill_preview', { p_tab_id: tabId }); const total = (data as { total_cents?: number } | null)?.total_cents ?? 0; setCharge({ amountCents: total, ref, tabId }); }

  if (loading || !restaurant) return <div className="center-note">{t('common.loading')}</div>;
  const currency = restaurant.currency;
  const locale = restaurant.locale;
  const current = selected ? sessions.find((s) => s.id === selected) : null;

  const chargeModal = charge && payCfg && (
    <div className="modal-overlay" onClick={() => setCharge(null)}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <PaymentQr amountCents={charge.amountCents} reference={charge.ref} cfg={payCfg} />
        {charge.tabId && (
          <button type="button" className="btn btn--primary btn--block" style={{ marginBottom: 8 }} onClick={() => void closeTab(charge.tabId as string, 'qr')}>{t('pay.paid_close')}</button>
        )}
        <button type="button" className="btn btn--ghost btn--block" onClick={() => setCharge(null)}>{t('pay.close')}</button>
      </div>
    </div>
  );

  // ---------- DETALHE DA MESA ----------
  if (current) {
    return (
      <div className="page">
        <button type="button" className="link-button" onClick={() => setSelected(null)}>{t('sessions.back')}</button>
        <h1 className="page__title">{t('sessions.table')} {current.label} {current.isFloating && <em className="tag">{t('sessions.floating_badge')}</em>}</h1>

        {current.ready.length > 0 && (
          <>
            <h2 className="section-title">{t('floor.ready')} <span className="tag">{current.ready.length}</span></h2>
            <div className="order-grid">
              {current.ready.map((it) => (
                <div key={it.id} className="order-card order-card--alert">
                  <div className="order-card__items">{it.qty}× {it.name}</div>
                  <button type="button" className="btn btn--primary btn--sm btn--block" onClick={() => void deliver(it.id)}>{t('floor.deliver')}</button>
                </div>
              ))}
            </div>
          </>
        )}

        <h2 className="section-title">{t('sessions.tabs')}</h2>
        <ul className="list">
          {current.tabs.map((tb) => (
            <li key={tb.id} className="tab-row">
              <div className="tab-row__head">
                <span>{t('sessions.tab')} {tb.seq} · {formatMoney(tb.total, currency, locale)}{tb.status === 'closed' && <em className="tag"> ✓</em>}</span>
                {tb.status === 'open' && (
                  <span className="row-actions">
                    <button type="button" className="link-button" onClick={() => setAddingFor(addingFor === tb.id ? null : tb.id)}>{t('sessions.add_item')}</button>
                    <button type="button" className="link-button" disabled={tb.total <= 0} onClick={() => void cobrarTab(tb.id, `${t('sessions.table')} ${current.label} ${tb.seq}`)}>{t('pay.charge')}</button>
                    <button type="button" className="link-button" onClick={() => void closeTab(tb.id)}>{t('sessions.close_tab')}</button>
                  </span>
                )}
              </div>
              {tb.status === 'open' && (
                <div className="tab-row__adj">
                  <label className="adj"><input type="checkbox" checked={tb.serviceApplied ?? serviceDefault} onChange={(e) => void updateTab(tb.id, { service_applied: e.target.checked })} />{t('sessions.service')}</label>
                  <label className="adj">{t('sessions.couvert')}<input type="number" min={0} value={tb.couvertQty} onChange={(e) => void updateTab(tb.id, { couvert_qty: Number(e.target.value) || 0 })} style={{ width: 52 }} /></label>
                  <select value={tb.bonifId ?? ''} onChange={(e) => void updateTab(tb.id, { bonification_id: e.target.value || null })}>
                    <option value="">{t('sessions.bonif')}: —</option>
                    {bonifList.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
                  </select>
                </div>
              )}
              {tb.status === 'open' && addingFor === tb.id && (
                <AddItemsPanel restaurantId={restaurant.id} tabId={tb.id} currency={currency} locale={locale} onDone={() => { setAddingFor(null); void reload(); }} />
              )}
            </li>
          ))}
        </ul>

        {moveTargets.length > 0 && (
          <div className="form-row" style={{ marginBottom: 8 }}>
            <select value={movePick} onChange={(e) => setMovePick(e.target.value)}>
              <option value="">{t('sessions.move_to')}…</option>
              {moveTargets.map((tb) => (<option key={tb.id} value={tb.id}>{tb.label}</option>))}
            </select>
            <button type="button" className="btn btn--ghost btn--sm" disabled={!movePick} onClick={() => void moveSession(current.id)}>{t('sessions.move')}</button>
          </div>
        )}
        <div className="form-row">
          <button type="button" className="btn btn--ghost btn--sm" disabled={current.total <= 0} onClick={() => setCharge({ amountCents: current.total, ref: `${t('sessions.table')} ${current.label}` })}>{t('pay.charge_total')}</button>
          <button type="button" className="btn btn--primary btn--sm" onClick={() => void closeSession(current.id)}>{t('sessions.end')}</button>
        </div>

        {chargeModal}
      </div>
    );
  }

  // ---------- GRID DE MESAS ----------
  return (
    <div className="page page--wide">
      <div className="page__head-row">
        <h1 className="page__title">{t('floor.title')}</h1>
        {readyTotal > 0 && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setSoundOn((x) => !x)}>{soundOn ? t('floor.sound_on') : t('floor.sound_off')}</button>
        )}
      </div>

      <div className="form-row" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        {moveTargets.length > 0 && (
          <span className="row-actions">
            <select value={openPick} onChange={(e) => setOpenPick(e.target.value)}>
              <option value="">{t('sessions.pick_table')}</option>
              {moveTargets.map((tb) => (<option key={tb.id} value={tb.id}>{tb.label}</option>))}
            </select>
            <button type="button" className="btn btn--ghost btn--sm" disabled={!openPick} onClick={() => void openTable()}>{t('sessions.open_table')}</button>
          </span>
        )}
        {floatTables.length > 0 && (
          <span className="row-actions">
            <select value={floatPick} onChange={(e) => setFloatPick(e.target.value)}>
              <option value="">{t('sessions.pick_floating')}</option>
              {floatTables.map((tb) => (<option key={tb.id} value={tb.id}>{tb.label}</option>))}
            </select>
            <button type="button" className="btn btn--ghost btn--sm" disabled={!floatPick} onClick={() => void openFloating()}>{t('sessions.open_floating')}</button>
          </span>
        )}
      </div>

      {sessions.length === 0 ? (
        <p className="muted-note">{t('sessions.none')}</p>
      ) : (
        <div className="order-grid">
          {sessions.map((s) => (
            <button key={s.id} type="button" className={`table-card${s.ready.length ? ' table-card--ready' : ''}`} onClick={() => setSelected(s.id)}>
              <div className="table-card__head">
                <strong>{t('sessions.table')} {s.label}</strong>
                <strong>{formatMoney(s.total, currency, locale)}</strong>
              </div>
              <div className="table-card__badges">
                {s.isFloating && <span className="tag">{t('sessions.floating_badge')}</span>}
                {s.billRequested && <span className="tag" style={{ background: 'var(--color-mostarda)', color: '#5a3e12' }}>🔔 {t('sessions.bill_requested')}</span>}
                {s.ready.length > 0 && <span className="tag" style={{ background: 'var(--color-terracota)', color: '#fff' }}>{s.ready.length} {t('sessions.ready_badge')}</span>}
              </div>
              <span className="table-card__cta">{t('sessions.access')} →</span>
            </button>
          ))}
        </div>
      )}

      {chargeModal}
    </div>
  );
}
